import { format } from 'date-fns';
import { Eye, FileClock, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useContractVersion, useContractVersions } from '@/hooks/commercial/use-contracts';
import type { ContractVersion } from '@/types/commercial/contracts';
import { buildClauseSections, buildSummaryRows } from './contract-version-snapshot';

interface ContractHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractLabel: string;
  contractId: string | null;
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

function ContractVersionDetail({ data }: { data: ContractVersion }) {
  const summaryRows = buildSummaryRows(data.type, data.summary);
  const clauseSections = buildClauseSections(data.clauses);

  return (
    <div className="space-y-6">
      <dl className="grid gap-3 sm:grid-cols-2">
        <HeaderField label="Versão" value={`v${data.version}`} />
        <HeaderField label="Publicado por" value={data.publishedBy.name} />
        <HeaderField
          label="Data e hora"
          value={format(new Date(data.publishedAt), "dd/MM/yyyy 'às' HH:mm")}
        />
        <HeaderField label="Cliente" value={data.accountName ?? '—'} />
        <HeaderField label="Documento do cliente" value={data.accountDocument ?? '—'} />
        <HeaderField label="Signatário" value={data.signerName ?? '—'} />
        <HeaderField label="Documento do signatário" value={data.signerDocumentation ?? '—'} />
      </dl>

      {summaryRows.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">Quadro-resumo</h4>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full table-fixed border-collapse text-sm">
              <tbody>
                {summaryRows.map((row) => (
                  <tr key={row.label} className="border-t border-border first:border-t-0">
                    <td className="w-1/2 bg-muted/30 p-3 align-top font-medium text-foreground">
                      {row.label}
                    </td>
                    <td className="w-1/2 whitespace-pre-wrap break-words p-3 align-top text-foreground">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {clauseSections.length > 0 && (
        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Cláusulas</h4>
          <div className="space-y-4">
            {clauseSections.map((section, index) => (
              <article key={`${section.title}-${index}`} className="space-y-1">
                <p className="text-sm font-semibold text-foreground">{section.title}</p>
                <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <p key={`${section.title}-${pIndex}`} className="break-words">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export function ContractHistoryDialog({
  open,
  onOpenChange,
  contractLabel,
  contractId,
}: ContractHistoryDialogProps) {
  const [page, setPage] = useState(1);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const history = useContractVersions(contractId, page, 10, open);
  const versionDetail = useContractVersion(contractId, selectedVersion);

  useEffect(() => {
    if (open) setPage(1);
    if (!open) setSelectedVersion(null);
  }, [open]);

  const entries = history.data?.docs ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileClock className="h-5 w-5 text-gray-700" />
            Histórico de versões
          </DialogTitle>
          <DialogDescription>
            Versões anteriores de <strong>{contractLabel}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto rounded-md border">
          {history.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.isError ? (
            <div className="py-8 text-center text-sm text-destructive">
              Não foi possível carregar o histórico.
            </div>
          ) : (
            <Table className="min-w-[620px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Versão</TableHead>
                  <TableHead>Autor</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-[100px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      Nenhuma versão registrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">v{entry.version}</TableCell>
                      <TableCell>{entry.publishedBy.name}</TableCell>
                      <TableCell>
                        {format(new Date(entry.publishedAt), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedVersion(entry.version)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Consultar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {(history.data?.pages ?? 0) > 1 && (
          <Pagination
            currentPage={page}
            totalPages={history.data?.pages ?? 1}
            totalItems={history.data?.total}
            limit={history.data?.limit}
            onPageChange={setPage}
          />
        )}

        {selectedVersion !== null && (
          <div className="rounded-md border p-4">
            {versionDetail.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando versão...
              </div>
            ) : versionDetail.data ? (
              <ContractVersionDetail data={versionDetail.data} />
            ) : (
              <span className="text-sm text-destructive">
                Não foi possível consultar esta versão.
              </span>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
